import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, User } from "lucide-react";
import { TID } from "../../lib/testIds";
import { useAuth } from "../../lib/auth";

const NAV_LINKS = [
  { to: "/", label: "Home", slug: "home" },
  { to: "/calculators", label: "Calculators", slug: "calculators" },
  { to: "/articles", label: "Articles", slug: "articles" },
  { to: "/pricing", label: "Pricing", slug: "pricing" },
  { to: "/borrow", label: "Borrow", slug: "borrow" },
  { to: "/invest", label: "Invest", slug: "invest" },
  { to: "/business", label: "Business", slug: "business" },
  { to: "/real-estate", label: "Real Estate", slug: "real-estate" },
  { to: "/vehicles", label: "Vehicles", slug: "vehicles" },
  { to: "/ai", label: "AI", slug: "ai" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, startLogin } = useAuth();

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/80 backdrop-blur-xl"
      role="banner"
    >
      <nav
        className="mml-container flex h-16 items-center justify-between gap-4"
        aria-label="Primary"
      >
        <Link
          to="/"
          data-testid={TID.navLogo}
          className="group flex items-center gap-2"
          onClick={() => setOpen(false)}
        >
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-700 text-white shadow-sm transition-transform group-hover:scale-105">
            <span className="font-bold leading-none">M</span>
          </span>
          <span className="font-bold tracking-tight text-slate-900">
            MyMoneyLocal
          </span>
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.slice(1, 9).map((l) => (
            <li key={l.slug}>
              <NavLink
                to={l.to}
                data-testid={TID.navLink(l.slug)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-emerald-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-emerald-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-2 lg:flex">
          {user?.is_admin && (
            <Link
              to="/admin/affiliates"
              data-testid="nav-admin-link"
              className="rounded-md px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
            >
              Admin
            </Link>
          )}
          {user ? (
            <Link
              to="/account"
              data-testid={TID.authNavAvatar}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:border-emerald-300 hover:bg-emerald-50/40"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-800">
                  <User className="h-3.5 w-3.5" />
                </span>
              )}
              <span className="max-w-[120px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => startLogin(pathname)}
              data-testid={TID.authLoginBtn}
              className="rounded-lg border border-emerald-700 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
            >
              Sign in
            </button>
          )}
          <Link
            to="/pricing"
            data-testid={TID.navNewsletterCta}
            className="mml-btn-primary px-4 py-2 text-sm"
          >
            See plans
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          type="button"
          data-testid={TID.navMobileToggle}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 place-items-center rounded-md text-slate-700 hover:bg-slate-50 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid={TID.navMobileMenu}
          className="border-t border-slate-200 bg-white lg:hidden"
        >
          <ul className="mml-container flex flex-col py-3">
            {NAV_LINKS.map((l) => (
              <li key={l.slug}>
                <Link
                  to={l.to}
                  data-testid={`mobile-${TID.navLink(l.slug)}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium ${
                    pathname === l.to
                      ? "bg-emerald-50 text-emerald-800"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {l.label}
                  <ArrowRight className="h-4 w-4 opacity-50" />
                </Link>
              </li>
            ))}
            <li className="mt-2">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mml-btn-primary w-full"
              >
                Contact us
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
